import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import {
  MetricCard,
  MetricLabel,
  MetricSub,
  MetricValue,
  Section,
  TableWrapper,
  getToneTokens,
} from "../../../styles/shared";

export const ZONE_TONES = {
  preto: "neutral",
  vermelho: "danger",
  amarelo: "warning",
  verde: "success",
};

export const ZONE_LABELS = {
  preto: "Pretos",
  vermelho: "Vermelhos",
  amarelo: "Amarelos",
  verde: "Verdes",
};

export const ZoneSwatch = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: ${({ theme, $zone }) =>
    getToneTokens(theme, ZONE_TONES[$zone] || "default").text};
`;

export const SimplifiedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const FullWidthSection = styled(Section)`
  width: 100%;
`;

export const HeroCard = styled(motion.div)`
  ${({ theme, $tone = "neutral" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}
  border-radius: 16px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const HeroEyebrow = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.85;
`;

export const HeroTitle = styled.div`
  font-family: var(--font-display);
  font-size: clamp(1.4rem, 2.4vw, 1.9rem);
  font-weight: 700;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: baseline;
`;

export const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme, $tone = "neutral" }) =>
    getToneTokens(theme, $tone).border};
  color: ${({ theme, $tone = "neutral" }) => getToneTokens(theme, $tone).text};
`;

export const HeroMeta = styled.div`
  font-size: 13px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  opacity: 0.9;
`;

export const HeroAction = styled.a`
  align-self: flex-start;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.accent};
  text-decoration: none;
  border-bottom: 1px dashed ${({ theme }) => theme.accent};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.accentDeep};
  }
`;

export const StackedCard = styled(MetricCard)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const StackedLabel = styled(MetricLabel)``;
export const StackedValue = styled(MetricValue)``;
export const StackedSub = styled(MetricSub)``;

export const ZoneLine = styled.div`
  display: grid;
  grid-template-columns: 18px 80px 1fr auto;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.textPrimary};
`;

export const ZoneCount = styled.span`
  font-weight: 700;
  text-align: right;
`;

export const StageBreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

export const StageBreakdownLine = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};

  & > strong {
    color: ${({ theme }) => theme.textPrimary};
    font-weight: 600;
  }
`;

export const ProgressTrack = styled.div`
  position: relative;
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.surfaceAlt};
  border-radius: 999px;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $pct = 0 }) => `${Math.min(100, Math.max(0, $pct * 100))}%`};
  background: ${({ theme, $tone = "success" }) =>
    getToneTokens(theme, $tone).text};
  transition: width 0.25s ease;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

export const SectionH2 = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const CriticosTableWrapper = styled(TableWrapper)``;

export const CriticosTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 760px;
`;

export const CriticosTh = styled.th`
  /* Bugfix #13 (2026-05-29): sticky header amplo. */
  position: sticky;
  top: var(--app-header-height, 0px);
  z-index: 5;
  padding: 10px 14px;
  text-align: ${({ $center, $right }) =>
    $center ? "center" : $right ? "right" : "left"};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.textSecondary};
  background: ${({ theme }) => theme.surfaceAlt};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  white-space: nowrap;
`;

export const CriticosTd = styled.td`
  padding: 10px 14px;
  text-align: ${({ $center, $right }) =>
    $center ? "center" : $right ? "right" : "left"};
  color: ${({ theme }) => theme.textPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

export const ZonePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  ${({ theme, $tone = "default" }) => {
    const t = getToneTokens(theme, $tone);
    return css`
      background: ${t.background};
      border: 1px solid ${t.border};
      color: ${t.text};
    `;
  }}
`;

export const PlaybookList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PlaybookItem = styled.details`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  padding: 12px 16px;

  & > summary {
    cursor: pointer;
    list-style: none;
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.textPrimary};
  }
  & > summary::-webkit-details-marker {
    display: none;
  }

  &[open] > summary {
    margin-bottom: 8px;
  }
`;

export const PlaybookStepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: ${({ theme }) => theme.accent};
  color: white;
  font-size: 12px;
  font-weight: 700;
`;

export const PlaybookBody = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  line-height: 1.55;
`;

export const PlaybookHint = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  font-style: italic;
`;

export const ChartWrapper = styled.div`
  width: 100%;
  height: 80px;
`;

export const ToneText = styled.span`
  color: ${({ theme, $tone = "default" }) => getToneTokens(theme, $tone).text};
  font-weight: 600;
`;

export const EmptyHint = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
`;

export const DrawerOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(15, 15, 20, 0.45);
  z-index: 50;
`;

export const DrawerPanel = styled(motion.aside)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(420px, 100%);
  background: ${({ theme }) => theme.surface};
  border-left: 1px solid ${({ theme }) => theme.border};
  box-shadow: -16px 0 40px rgba(15, 15, 20, 0.18);
  display: flex;
  flex-direction: column;
  z-index: 51;
`;

export const DrawerHeader = styled.div`
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

export const DrawerTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
`;

export const DrawerCloseBtn = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    border-color: ${({ theme }) => theme.textSecondary};
  }
`;

export const DrawerBody = styled.div`
  padding: 16px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const DrawerFooter = styled.div`
  padding: 12px 20px;
  border-top: 1px solid ${({ theme }) => theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const DrawerSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const DrawerSectionTitle = styled.h3`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};
`;

export const FieldRow = styled.label`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  font-size: 13px;
  color: ${({ theme }) => theme.textPrimary};
`;

export const NumberInput = styled.input`
  width: 110px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 13px;
  text-align: right;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
  }
`;

export const Select = styled.select`
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 13px;
`;

export const SliderRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 56px;
  gap: 10px;
  align-items: center;
  font-size: 13px;
  color: ${({ theme }) => theme.textPrimary};
`;

export const Slider = styled.input.attrs({ type: "range" })`
  width: 100%;
`;

export const TotalLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px dashed ${({ theme }) => theme.border};
  padding-top: 8px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
`;

export const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.accent};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.accentDeep};
  }
`;

export const GhostButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    border-color: ${({ theme }) => theme.textSecondary};
  }
`;

export const IconButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    border-color: ${({ theme }) => theme.textSecondary};
  }
`;

export const HelperText = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.textSecondary};
`;

export const BannerWrapper = styled(motion.div)`
  ${({ theme }) => {
    const tokens = getToneTokens(theme, "warning");
    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}
  border-radius: 12px;
  padding: 12px 16px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
  font-size: 13px;
`;

export const BannerStrong = styled.strong`
  font-weight: 700;
`;

export const BannerCloseBtn = styled.button`
  background: transparent;
  color: inherit;
  border: 1px solid currentColor;
  border-radius: 999px;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;
